import { Dispatch, SetStateAction, useCallback, useState } from "react";

import {
	useBulkApproveCaseStudiesMutation,
	useBulkApproveIcpsMutation,
	useBulkApproveObjectionsMutation,
	useBulkApproveProductsMutation,
	useCreateCaseStudyMutation,
	useCreateKnowledgeDbProductMutation,
	useCreateObjectionCategoryMutation,
	useCreateObjectionMutation,
	useDeleteCaseStudyMutation,
	useDeleteKnowledgeDbProductMutation,
	useDeleteObjectionCategoryMutation,
	useDeleteObjectionMutation,
	useTriggerCaseStudyAutoUpdateMutation,
	useUpdateCaseStudyMutation,
	useUpdateIcpMutation,
	useUpdateKnowledgeDbProductMutation,
	useUpdateObjectionMutation,
	useUpsertSettingsMutation,
} from "@/pages/Private/redux/knowledgeDb/knowledgeDb.api";

import { SegmentContent } from "../schema/knowledgeDb";
import { CreateProduct, Product } from "../schema/productsServices";
import { AutoUpdateSettings, CaseStudy, CreateCaseStudy } from "../schema/caseStudies";
import {
	CreateCategory,
	CreateObjection,
	Objection,
	ObjectionCategory,
} from "../schema/objectionHandling";
import {
	caseStudyToApiPayload,
	objectionToApiPayload,
	productToApiPayload,
	segmentContentToApiPayload,
	toApiJsonWrapper,
} from "../utils/transformers";

interface UseKnowledgeDbHandlersOptions {
	objectionCategories: ObjectionCategory[];
	setActiveProductId: Dispatch<SetStateAction<number | null>>;
	setActiveCaseStudyId: Dispatch<SetStateAction<number | null>>;
	setActiveCategoryId: Dispatch<SetStateAction<number | null>>;
	/** Fired after every persisted change — the onboarding tour tracks "edited during visit" through it. */
	onMutated: () => void;
}

const toggleInSet = (previous: Set<number>, id: number): Set<number> => {
	const next = new Set(previous);

	if (next.has(id)) {
		next.delete(id);
	} else {
		next.add(id);
	}

	return next;
};

/**
 * All Knowledge DB mutations, their handlers and the per-tab bulk-approve
 * selection state, extracted from KnowledgeDb.tsx (file-length ratchet).
 * Every successful write funnels through `onMutated`.
 */
export const useKnowledgeDbHandlers = ({
	objectionCategories,
	setActiveProductId,
	setActiveCaseStudyId,
	setActiveCategoryId,
	onMutated,
}: UseKnowledgeDbHandlersOptions) => {
	const [updateIcp] = useUpdateIcpMutation();
	const [createProduct] = useCreateKnowledgeDbProductMutation();
	const [updateProduct] = useUpdateKnowledgeDbProductMutation();
	const [deleteProduct] = useDeleteKnowledgeDbProductMutation();
	const [createCaseStudy] = useCreateCaseStudyMutation();
	const [updateCaseStudy] = useUpdateCaseStudyMutation();
	const [deleteCaseStudy] = useDeleteCaseStudyMutation();
	const [upsertSettings] = useUpsertSettingsMutation();
	const [createObjectionCategory] = useCreateObjectionCategoryMutation();
	const [deleteObjectionCategory] = useDeleteObjectionCategoryMutation();
	const [createObjection] = useCreateObjectionMutation();
	const [updateObjection] = useUpdateObjectionMutation();
	const [deleteObjection] = useDeleteObjectionMutation();
	const [triggerCaseStudyAutoUpdate] = useTriggerCaseStudyAutoUpdateMutation();
	const [bulkApproveIcps] = useBulkApproveIcpsMutation();
	const [bulkApproveProducts] = useBulkApproveProductsMutation();
	const [bulkApproveCaseStudies] = useBulkApproveCaseStudiesMutation();
	const [bulkApproveObjections] = useBulkApproveObjectionsMutation();

	const [selectedSegmentIds, setSelectedSegmentIds] = useState<Set<number>>(new Set());
	const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set());
	const [selectedCaseStudyIds, setSelectedCaseStudyIds] = useState<Set<number>>(new Set());
	const [selectedObjectionIds, setSelectedObjectionIds] = useState<Set<number>>(new Set());

	// ── ICP Handlers ──
	const handleContentChange = useCallback(
		async (updatedContent: SegmentContent) => {
			await updateIcp({
				id: updatedContent.id,
				body: segmentContentToApiPayload(updatedContent),
			});
			onMutated();
		},
		[updateIcp, onMutated]
	);

	const handleToggleSegmentSelection = useCallback((contentId: number) => {
		setSelectedSegmentIds((prev) => toggleInSet(prev, contentId));
	}, []);

	const handleSelectAllSegments = useCallback((ids: number[]) => {
		setSelectedSegmentIds(new Set(ids));
	}, []);

	const handleBulkApprove = useCallback(async () => {
		if (selectedSegmentIds.size === 0) {
			return;
		}

		await bulkApproveIcps({ ids: Array.from(selectedSegmentIds) });
		setSelectedSegmentIds(new Set());
		onMutated();
	}, [selectedSegmentIds, bulkApproveIcps, onMutated]);

	// ── Product Handlers ──
	const handleToggleProductSelection = useCallback((productId: number) => {
		setSelectedProductIds((prev) => toggleInSet(prev, productId));
	}, []);

	const handleSelectAllProducts = useCallback((ids: number[]) => {
		setSelectedProductIds(new Set(ids));
	}, []);

	const handleProductApprove = useCallback(
		async (productId: number) => {
			await bulkApproveProducts({ ids: [productId] });
			onMutated();
		},
		[bulkApproveProducts, onMutated]
	);

	const handleBulkApproveProducts = useCallback(async () => {
		if (selectedProductIds.size === 0) {
			return;
		}

		await bulkApproveProducts({ ids: Array.from(selectedProductIds) });
		setSelectedProductIds(new Set());
		onMutated();
	}, [selectedProductIds, bulkApproveProducts, onMutated]);

	const handleProductChange = useCallback(
		async (updated: Product) => {
			await updateProduct({ id: updated.id, body: productToApiPayload(updated) });
			onMutated();
		},
		[updateProduct, onMutated]
	);

	const handleProductUnapprove = useCallback(
		async (productId: number) => {
			await updateProduct({ id: productId, body: { status: "DRAFT" } });
			onMutated();
		},
		[updateProduct, onMutated]
	);

	const handleProductDelete = useCallback(
		async (productId: number) => {
			await deleteProduct(productId);
			setActiveProductId((current) => (current === productId ? null : current));
			onMutated();
		},
		[deleteProduct, setActiveProductId, onMutated]
	);

	const handleCreateProduct = useCallback(
		async (data: CreateProduct, withAi: boolean) => {
			const result = await createProduct({
				name: data.name,
				type: data.type,
				description: data.description,
				...(withAi && {
					keyFeatures: toApiJsonWrapper(["AI-generated feature 1", "AI-generated feature 2"]),
				}),
			});

			if ("data" in result && result.data) {
				setActiveProductId(result.data.id);
			}

			onMutated();
		},
		[createProduct, setActiveProductId, onMutated]
	);

	// ── Case Study Handlers ──
	const handleToggleCaseStudySelection = useCallback((caseStudyId: number) => {
		setSelectedCaseStudyIds((prev) => toggleInSet(prev, caseStudyId));
	}, []);

	const handleSelectAllCaseStudies = useCallback((ids: number[]) => {
		setSelectedCaseStudyIds(new Set(ids));
	}, []);

	const handleCaseStudyApprove = useCallback(
		async (caseStudyId: number) => {
			await bulkApproveCaseStudies({ ids: [caseStudyId] });
			onMutated();
		},
		[bulkApproveCaseStudies, onMutated]
	);

	const handleBulkApproveCaseStudies = useCallback(async () => {
		if (selectedCaseStudyIds.size === 0) {
			return;
		}

		await bulkApproveCaseStudies({ ids: Array.from(selectedCaseStudyIds) });
		setSelectedCaseStudyIds(new Set());
		onMutated();
	}, [selectedCaseStudyIds, bulkApproveCaseStudies, onMutated]);

	const handleCaseStudyChange = useCallback(
		async (updated: CaseStudy) => {
			await updateCaseStudy({ id: updated.id, body: caseStudyToApiPayload(updated) });
			onMutated();
		},
		[updateCaseStudy, onMutated]
	);

	const handleCaseStudyUnapprove = useCallback(
		async (caseStudyId: number) => {
			await updateCaseStudy({ id: caseStudyId, body: { status: "DRAFT" } });
			onMutated();
		},
		[updateCaseStudy, onMutated]
	);

	const handleCaseStudyDelete = useCallback(
		async (caseStudyId: number) => {
			await deleteCaseStudy(caseStudyId);
			setActiveCaseStudyId((current) => (current === caseStudyId ? null : current));
			onMutated();
		},
		[deleteCaseStudy, setActiveCaseStudyId, onMutated]
	);

	const handleCreateCaseStudy = useCallback(
		async (data: CreateCaseStudy) => {
			const result = await createCaseStudy({
				clientName: data.clientName,
				website: data.website ?? "",
				goalsChallenge: data.goalsChallenge,
				solution: data.solution,
				outcome: data.outcome,
				quote: data.quoteText ?? "",
				quoteAttribution: data.quoteAttribution ?? "",
				...(data.autoEnrich && { enrichmentSource: "linkedin" }),
			});

			if ("data" in result && result.data) {
				setActiveCaseStudyId(result.data.id);
			}

			onMutated();
		},
		[createCaseStudy, setActiveCaseStudyId, onMutated]
	);

	const handleSettingsChange = useCallback(
		async (settings: AutoUpdateSettings) => {
			await upsertSettings({
				autoUpdateEnabled: settings.autoUpdateEnabled,
				caseStudyUrl: settings.caseStudyUrl,
				caseStudyDocumentUrl: settings.caseStudyDocumentUrl,
			});
			onMutated();
		},
		[upsertSettings, onMutated]
	);

	const handleTriggerManualUpdate = useCallback(async () => {
		await triggerCaseStudyAutoUpdate();
	}, [triggerCaseStudyAutoUpdate]);

	// ── Objection Handlers ──
	const handleToggleObjectionSelection = useCallback((objectionId: number) => {
		setSelectedObjectionIds((prev) => toggleInSet(prev, objectionId));
	}, []);

	const handleSelectAllObjections = useCallback((ids: number[]) => {
		setSelectedObjectionIds(new Set(ids));
	}, []);

	const handleObjectionApprove = useCallback(
		async (objectionId: number) => {
			await bulkApproveObjections({ ids: [objectionId] });
			onMutated();
		},
		[bulkApproveObjections, onMutated]
	);

	const handleBulkApproveObjections = useCallback(async () => {
		if (selectedObjectionIds.size === 0) {
			return;
		}

		await bulkApproveObjections({ ids: Array.from(selectedObjectionIds) });
		setSelectedObjectionIds(new Set());
		onMutated();
	}, [selectedObjectionIds, bulkApproveObjections, onMutated]);

	const handleObjectionChange = useCallback(
		async (updated: Objection) => {
			await updateObjection({ id: updated.id, body: objectionToApiPayload(updated) });
			onMutated();
		},
		[updateObjection, onMutated]
	);

	const handleObjectionUnapprove = useCallback(
		async (objectionId: number) => {
			await updateObjection({ id: objectionId, body: { status: "DRAFT" } });
			onMutated();
		},
		[updateObjection, onMutated]
	);

	const handleObjectionDelete = useCallback(
		async (objectionId: number) => {
			await deleteObjection(objectionId);
			onMutated();
		},
		[deleteObjection, onMutated]
	);

	const handleCreateObjection = useCallback(
		async (data: CreateObjection, withAi: boolean) => {
			const strategies =
				data.initialStrategyName && data.initialStrategyName.trim()
					? [
							{
								id: Date.now(),
								strategyName: data.initialStrategyName,
								approach: data.initialApproach ?? "",
								exampleReply: data.initialExampleReply ?? "",
							},
						]
					: withAi
						? [
								{
									id: Date.now(),
									strategyName: "AI-generated strategy",
									approach: "AI-generated approach pending...",
									exampleReply: "AI-generated example reply pending...",
								},
							]
						: [];

			await createObjection({
				categoryId: data.categoryId,
				quote: data.quote,
				underlyingConcern: data.underlyingConcern,
				strategies: toApiJsonWrapper(strategies),
			});
			onMutated();
		},
		[createObjection, onMutated]
	);

	const handleCreateCategory = useCallback(
		async (data: CreateCategory) => {
			await createObjectionCategory({ name: data.name, isCustom: true });
			onMutated();
		},
		[createObjectionCategory, onMutated]
	);

	const handleDeleteCategory = useCallback(
		async (categoryId: number) => {
			await deleteObjectionCategory(categoryId);
			setActiveCategoryId((current) => {
				if (current === categoryId) {
					const remaining = objectionCategories.filter((cat) => cat.id !== categoryId);

					return remaining[0]?.id ?? null;
				}

				return current;
			});
			onMutated();
		},
		[deleteObjectionCategory, objectionCategories, setActiveCategoryId, onMutated]
	);

	return {
		selectedSegmentIds,
		selectedProductIds,
		selectedCaseStudyIds,
		selectedObjectionIds,
		handleContentChange,
		handleToggleSegmentSelection,
		handleSelectAllSegments,
		handleBulkApprove,
		handleToggleProductSelection,
		handleSelectAllProducts,
		handleProductApprove,
		handleBulkApproveProducts,
		handleProductChange,
		handleProductUnapprove,
		handleProductDelete,
		handleCreateProduct,
		handleToggleCaseStudySelection,
		handleSelectAllCaseStudies,
		handleCaseStudyApprove,
		handleBulkApproveCaseStudies,
		handleCaseStudyChange,
		handleCaseStudyUnapprove,
		handleCaseStudyDelete,
		handleCreateCaseStudy,
		handleSettingsChange,
		handleTriggerManualUpdate,
		handleToggleObjectionSelection,
		handleSelectAllObjections,
		handleObjectionApprove,
		handleBulkApproveObjections,
		handleObjectionChange,
		handleObjectionUnapprove,
		handleObjectionDelete,
		handleCreateObjection,
		handleCreateCategory,
		handleDeleteCategory,
	};
};
